import React, { useState, useEffect } from 'react';
import JobForm from './JobForm';
import ConfirmDialog from '../common/ConfirmDialog';
import Select2Dropdown from '../common/Select2Dropdown';
import { toast } from 'react-toastify';
import './JobList.css';
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import

const API_BASE_URL = '/api/documentation/jobs/';

const JobList = () => {
    const { hasPermission } = usePermissions(); // 2. Get the permission checker function
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchInput, setSearchInput] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
    const [expandedRows, setExpandedRows] = useState(new Set());
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 25;
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'desc' });

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentJob, setCurrentJob] = useState(null);
    const [formMode, setFormMode] = useState('add'); // 'add', 'edit', 'view'

    // Change Creator modal state
    const [creatorModal, setCreatorModal] = useState({ open: false, job: null });
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [savingCreator, setSavingCreator] = useState(false);
    const [confirmCreatorModal, setConfirmCreatorModal] = useState({ open: false, userId: null, userName: '' });
    const [confirmToggleActive, setConfirmToggleActive] = useState({ open: false, job: null });
    const [confirmDelete, setConfirmDelete] = useState({ open: false, job: null });

    // Add Nomination modal state
    const [nominationModal, setNominationModal] = useState(false);
    const [nominationForm, setNominationForm] = useState({ bl_number: '', customer: '', vessel: '', eta: '', icd: '', folder_name: '', shipping_line: '' });
    const [nomCustomers, setNomCustomers] = useState([]);
    const [nomVessels, setNomVessels] = useState([]);
    const [nomIcds, setNomIcds] = useState([]);
    const [nomShippingLines, setNomShippingLines] = useState([]);
    const [nomSaving, setNomSaving] = useState(false);

    const fetchJobDetails = async (jobId) => {
        const response = await fetch(`${API_BASE_URL}${jobId}/`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('kk_token')}`,
            },
        });

        if (!response.ok) {
            throw new Error('Failed to load full job details');
        }

        return response.json();
    };

    useEffect(() => {
        fetchJobs(currentPage, appliedSearchTerm, sortConfig);
    }, [currentPage, appliedSearchTerm, sortConfig]);

    const fetchJobs = async (page = 1, search = '', sort = { key: 'id', direction: 'desc' }) => {
        setLoading(true);
        setError(null);
        try {
            const ordering = sort.direction === 'desc' ? `-${sort.key}` : sort.key;
            const params = new URLSearchParams({
                page: String(page),
                page_size: String(itemsPerPage),
                ordering,
            });

            if (search && search.trim()) {
                params.append('search', search.trim());
            }

            const response = await fetch(`${API_BASE_URL}?${params.toString()}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('kk_token')}`,
                },
            });
            if (!response.ok) {
                throw new Error('Failed to fetch jobs');
            }
            const data = await response.json();
            if (Array.isArray(data)) {
                setJobs(data);
                setTotalCount(data.length);
                setTotalPages(Math.max(1, Math.ceil(data.length / itemsPerPage)));
            } else {
                setJobs(data.results || []);
                setTotalCount(data.count || 0);
                setTotalPages(Math.max(1, Math.ceil((data.count || 0) / itemsPerPage)));
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAddJob = () => {
        // Safeguard check
        if (!hasPermission('JOBS', 'can_create')) {
            alert("You do not have permission to add a new job.");
            return;
        }
        setCurrentJob(null);
        setFormMode('add');
        setIsModalOpen(true);
    };

    const handleViewJob = async (job) => {
        try {
            const fullJob = await fetchJobDetails(job.id);
            setCurrentJob(fullJob);
            setFormMode('view');
            setIsModalOpen(true);
        } catch (err) {
            setError(err.message || 'Failed to load job details');
        }
    };

    const handleEditJob = async (job) => {
        // Safeguard check
        if (!hasPermission('JOBS', 'can_edit')) {
            alert("You do not have permission to edit this job.");
            return;
        }

        try {
            const fullJob = await fetchJobDetails(job.id);
            setCurrentJob(fullJob);
            setFormMode('edit');
            setIsModalOpen(true);
        } catch (err) {
            setError(err.message || 'Failed to load job details');
        }
    };

    const handleDeleteJob = async (job) => {
        // 3. Add permission check at the start of the handler
        if (!hasPermission('JOBS', 'can_delete')) {
            alert("You do not have permission to delete this job.");
            return;
        }

        // Jobs with any payable entry cannot be deleted.
        try {
            const res = await fetch(`${API_BASE_URL}${job.id}/has-payables/`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('kk_token')}` },
            });
            if (res.ok) {
                const data = await res.json();
                if (data.has_payables) {
                    toast.error('This job cannot be deleted since it has a payable entry.');
                    return;
                }
            }
        } catch (e) {
            // If the check fails, fall through to the confirm dialog — the
            // backend still guards the delete itself.
        }

        setConfirmDelete({ open: true, job });
    };

    const handleConfirmDelete = async () => {
        const job = confirmDelete.job;
        if (!job) return;
        setConfirmDelete({ open: false, job: null });

        try {
            const response = await fetch(`${API_BASE_URL}${job.id}/`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('kk_token')}`,
                },
            });

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to delete job');
            }

            fetchJobs(currentPage, appliedSearchTerm, sortConfig); // Refresh the list
        } catch (err) {
            setError(err.message);
        }
    };

    const handleOpenNomination = async () => {
        if (!hasPermission('JOBS', 'can_create')) {
            alert('You do not have permission to add a nomination.');
            return;
        }
        setNominationForm({ bl_number: '', customer: '', vessel: '', eta: '', icd: '', folder_name: '', shipping_line: '' });
        setNominationModal(true);
        const authH = { 'Authorization': `Bearer ${localStorage.getItem('kk_token')}` };
        try {
            const [custRes, vesRes, icdRes, slRes] = await Promise.all([
                fetch('/api/masters/customers/', { headers: authH }),
                fetch('/api/documentation/nominations/vessels/', { headers: authH }),
                fetch('/api/masters/icds/', { headers: authH }),
                fetch('/api/masters/shippinglines/', { headers: authH }),
            ]);
            if (custRes.ok) {
                const d = await custRes.json();
                const list = d.results || d || [];
                setNomCustomers(list.map(c => ({ id: c.id, text: c.name })));
            }
            if (vesRes.ok) {
                const d = await vesRes.json();
                const vessels = (d.vessels || d.unique_vessels || []).map(v => ({ id: v, text: v }));
                if (!vessels.some(v => (v.id || '').toUpperCase() === 'UNKNOWN')) {
                    vessels.push({ id: 'UNKNOWN', text: 'UNKNOWN' });
                }
                setNomVessels(vessels);
            }
            if (icdRes.ok) {
                const d = await icdRes.json();
                const list = d.results || d || [];
                setNomIcds(list.map(c => ({ id: c.id, text: c.name })));
            }
            if (slRes.ok) {
                const d = await slRes.json();
                const list = d.results || d || [];
                // Only actual shipping lines — exclude LOCAL AGENT/CONSOLIDATOR entries
                setNomShippingLines(
                    list.filter(s => s.line_type === 'SHIPPING LINE')
                        .map(s => ({ id: s.id, text: s.name }))
                );
            }
        } catch (e) {
            toast.error('Failed to load nomination options.');
        }
    };

    const handleSaveNomination = async () => {
        const f = nominationForm;
        if (!f.bl_number.trim()) { toast.error('BL Number is required.'); return; }
        if (!f.customer) { toast.error('Customer is required.'); return; }
        if (!f.folder_name.trim()) { toast.error('Folder is required.'); return; }
        if (!f.shipping_line) { toast.error('Shipping Line is required.'); return; }
        // Vessel may be unknown; ETA is only mandatory once a real vessel is chosen
        if (f.vessel && f.vessel !== 'UNKNOWN' && !f.eta) { toast.error('ETA is required when a Vessel is selected.'); return; }
        if (f.eta) {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            if (f.eta < tomorrow.toISOString().split('T')[0]) { toast.error('ETA must be a future date.'); return; }
        }
        if (!f.icd) { toast.error('Nominated ICD is required.'); return; }

        setNomSaving(true);
        try {
            const res = await fetch('/api/documentation/nominations/', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('kk_token')}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    bl_number: f.bl_number.trim(),
                    customer: f.customer,
                    vessel: f.vessel,
                    eta: f.eta || null,
                    icd: f.icd,
                    folder_name: f.folder_name.trim(),
                    shipping_line: f.shipping_line || null,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.detail || data.eta?.[0] || 'Failed to save nomination.');
            toast.success('Nomination saved.');
            setNominationModal(false);
        } catch (err) {
            toast.error(err.message || 'Failed to save nomination.');
        } finally {
            setNomSaving(false);
        }
    };

    const handleToggleActive = (job) => {
        if (!hasPermission('JOBS', 'can_delete')) {
            alert('You do not have permission to perform this action.');
            return;
        }
        setConfirmToggleActive({ open: true, job });
    };

    const handleConfirmToggleActive = async () => {
        const job = confirmToggleActive.job;
        if (!job) return;
        setConfirmToggleActive({ open: false, job: null });
        const activating = job.is_active === false;
        const verb = activating ? 'activate' : 'inactivate';

        try {
            const response = await fetch(`${API_BASE_URL}${job.id}/set-active/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('kk_token')}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ is_active: activating }),
            });

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.error || `Failed to ${verb} job`);
            }

            fetchJobs(currentPage, appliedSearchTerm, sortConfig); // Refresh the list
        } catch (err) {
            setError(err.message);
        }
    };

    const handleFormClose = (refresh = false) => {
        setIsModalOpen(false);
        setCurrentJob(null);
        if (refresh) {
            fetchJobs(currentPage, appliedSearchTerm, sortConfig);
        }
    };

    const handleOpenChangeCreator = async (job) => {
        setCreatorModal({ open: true, job });
        setSelectedUserId('');
        setLoadingUsers(true);
        try {
            const res = await fetch('/api/admin/users/?active=true', {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('kk_token')}` },
            });
            if (res.ok) {
                const data = await res.json();
                const list = Array.isArray(data) ? data : (data.results || []);
                setUsers(list);
            }
        } catch (err) {
            console.error('Failed to fetch users', err);
        } finally {
            setLoadingUsers(false);
        }
    };

    const handleSaveCreator = () => {
        if (!selectedUserId) return;
        const selectedUser = users.find(u => String(u.id) === String(selectedUserId));
        const displayName = selectedUser
            ? [selectedUser.first_name, selectedUser.last_name].filter(Boolean).join(' ') || selectedUser.username
            : `User #${selectedUserId}`;
        setConfirmCreatorModal({ open: true, userId: selectedUserId, userName: displayName });
    };

    const handleConfirmCreatorChange = async () => {
        setSavingCreator(true);
        try {
            const res = await fetch(`${API_BASE_URL}${creatorModal.job.id}/change-creator/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('kk_token')}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ user_id: confirmCreatorModal.userId }),
            });
            if (res.ok) {
                setConfirmCreatorModal({ open: false, userId: null, userName: '' });
                setCreatorModal({ open: false, job: null });
                fetchJobs(currentPage, appliedSearchTerm, sortConfig);
            } else {
                const err = await res.json();
                alert(err.error || 'Failed to update creator.');
            }
        } catch (err) {
            alert('Failed to update creator.');
        } finally {
            setSavingCreator(false);
        }
    };

    const handleSearchChange = (e) => {
        setSearchInput(e.target.value);
    };

    const handleSearchClick = () => {
        setCurrentPage(1); // Reset to first page on search
        setAppliedSearchTerm(searchInput);
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearchClick();
        }
    };

    const handleSearchClear = () => {
        setSearchInput('');
        setAppliedSearchTerm('');
        setCurrentPage(1);
    };

    const toggleRowExpansion = (jobId) => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            if (next.has(jobId)) next.delete(jobId);
            else next.add(jobId);
            return next;
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString();
    };

    // "3 (2 x 40FT HC, 1 x 20FT)" — backend summary on list rows, derived from
    // the nested containers array on full-serializer payloads
    const containerSummary = (job) => {
        if (job.container_summary) return job.container_summary;
        const containers = job.containers || [];
        if (!containers.length) return '';
        const sizeMap = {};
        containers.forEach(c => {
            const key = `${(c.size || '').toUpperCase()} ${(c.container_type || '').toUpperCase()}`.trim();
            if (key) sizeMap[key] = (sizeMap[key] || 0) + 1;
        });
        const parts = Object.entries(sizeMap).map(([size, count]) => `${count} x ${size}`);
        return parts.length ? `${containers.length} (${parts.join(', ')})` : String(containers.length);
    };

    const renderBlNumber = (job) => {
        const isLcl = (job.shipment_type || '').toString().toUpperCase() === 'LCL' ||
                      (job.cargo_type || '').toString().toUpperCase() === 'LCL';
        const isFcl = (job.shipment_type || '').toString().toUpperCase() === 'FCL' ||
                      (job.cargo_type || '').toString().toUpperCase() === 'FCL';

        if (isLcl) {
            const hbl = job.hbl_so_no || '-';
            const mbl = job.mbl_no || '-';
            return <span title={`MBL: ${mbl}`}>{hbl}</span>;
        }

        const bl = job.mbl_no || '-';
        if (isFcl) {
            const summary = containerSummary(job);
            if (summary) {
                return <span title={`Containers: ${summary}`}>{bl}</span>;
            }
        }
        return bl;
    };

    const indexOfFirstJob = totalCount === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const indexOfLastJob = Math.min(currentPage * itemsPerPage, totalCount);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const requestSort = (key) => {
        setCurrentPage(1);
        setSortConfig(prev => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return '';
        return sortConfig.direction === 'asc' ? '▲' : '▼';
    };

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;

        if (totalPages <= maxVisible + 2) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
            return pages;
        }

        pages.push(1);
        let start = Math.max(2, currentPage - 1);
        let end = Math.min(totalPages - 1, currentPage + 1);

        if (currentPage <= 3) {
            start = 2;
            end = 4;
        } else if (currentPage >= totalPages - 2) {
            start = totalPages - 3;
            end = totalPages - 1;
        }

        if (start > 2) pages.push('...');
        for (let i = start; i <= end; i++) pages.push(i);
        if (end < totalPages - 1) pages.push('...');
        pages.push(totalPages);

        return pages;
    };

    // 4. Add a top-level permission check for viewing the page
    if (!hasPermission('JOBS', 'can_view')) {
        return (
            <div className="job-list-container">
                <h2>Permission Denied</h2>
                <p>You do not have permission to view this page.</p>
            </div>
        );
    }

    return (
        <div className="job-list-container">
            <h2>Jobs List</h2>

            {loading && <p className="loading-message">Loading jobs...</p>}
            {error && <p className="error-message">{error}</p>}

            <div className="toolbar">
                <div className="toolbar-search-group">
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{ fontSize: '11px', color: '#888', marginBottom: '3px' }}>
                            Search by: Job ID, Customer, UCR, IDF, TANSAD, MBL/HBL, Customer Ref, Email Subject, Container No
                        </div>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <input
                                type="text"
                                placeholder="Search jobs..."
                                value={searchInput}
                                onChange={handleSearchChange}
                                onKeyDown={handleSearchKeyDown}
                                className="search-input"
                            />
                            <button type="button" onClick={handleSearchClick}>Search</button>
                            <button type="button" onClick={handleSearchClear}>Clear</button>
                        </div>
                    </div>
                </div>
                <div className="toolbar-actions-group" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                    {/* 5. Conditionally render the "Add New Job" button */}
                    {hasPermission('JOBS', 'can_create') && (
                        <button onClick={handleOpenNomination} className="add-btn" style={{ backgroundColor: '#e67e22' }}>Add Nomination</button>
                    )}
                    {hasPermission('JOBS', 'can_create') && (
                        <button onClick={handleAddJob} className="add-btn">Add New Job</button>
                    )}
                </div>
            </div>

            {!loading && !error && (
                jobs.length > 0 ? (
                    <>
                    <div style={{ overflowX: 'auto' }}>
                        <table className="jobs-table">
                        <thead>
                            <tr>
                                <th onClick={() => requestSort('id')} className="sortable">ID {getSortIndicator('id')}</th>
                                <th onClick={() => requestSort('customer_name')} className="sortable">Customer {getSortIndicator('customer_name')}</th>
                                <th onClick={() => requestSort('ucr_no')} className="sortable">UCR No. {getSortIndicator('ucr_no')}</th>
                                <th onClick={() => requestSort('idf_no')} className="sortable">IDF No. {getSortIndicator('idf_no')}</th>
                                <th onClick={() => requestSort('tansad_no')} className="sortable">Tansad No. {getSortIndicator('tansad_no')}</th>
                                <th onClick={() => requestSort('tansad_date')} className="sortable">Date of Submission {getSortIndicator('tansad_date')}</th>
                                <th onClick={() => requestSort('mbl_no')} className="sortable">BL No {getSortIndicator('mbl_no')}</th>
                                <th onClick={() => requestSort('customer_ref')} className="sortable">Customer Ref {getSortIndicator('customer_ref')}</th>
                                {/* <th onClick={() => requestSort('receipt_date')} className="sortable">
                                    Receipt Date {getSortIndicator('receipt_date')}
                                </th> */}
                                <th onClick={() => requestSort('created_by_name')} className="sortable">Created By {getSortIndicator('created_by_name')}</th>
                                <th onClick={() => requestSort('created_at')} className="sortable">Created On {getSortIndicator('created_at')}</th>
                                <th>Details</th>
                                {/* 6. Conditionally render the Actions column header if user lacks edit/delete rights */}
                                {(hasPermission('JOBS', 'can_edit') || hasPermission('JOBS', 'can_delete') || hasPermission('JOBS', 'can_view')) && (
                                    <th>Actions</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {jobs.map((job) => (
                                <React.Fragment key={job.id}>
                                    <tr className={job.is_active === false ? 'job-inactive-row' : ''} title={job.is_active === false ? 'This job is inactive' : undefined}>
                                        <td className="job-id">{job.id || '-'}</td>
                                        <td className="customer-name"><strong>{job.customer_name || '-'}</strong></td>
                                        <td>{job.ucr_no || '-'}</td>
                                        <td>{job.idf_no || '-'}</td>
                                        <td title={job.status_remark.toUpperCase() || ''}>{job.tansad_no || '-'}</td>
                                        <td>{formatDate(job.tansad_date)}</td>
                                        <td>{renderBlNumber(job)}</td>
                                        <td>{job.customer_ref || '-'}</td>
                                        {/* <td>{formatDate(job.receipt_date)}</td> */}
                                        <td>{job.created_by_name || '-'}</td>
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            {(() => {
                                                if (!job.created_at) return '-';
                                                const d = new Date(job.created_at);
                                                if (isNaN(d)) return '-';
                                                const pad = n => n.toString().padStart(2, '0');
                                                const datePart = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
                                                const timePart = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
                                                return (<span>{datePart}<br /><span style={{ fontSize: 11, color: '#555' }}>{timePart}</span></span>);
                                            })()}
                                        </td>
                                        <td className="expand-toggle">
                                            {(job.containers?.length > 0 || job.notes?.length > 0 || job.commodities?.length > 0 || job.email_subject || job.container_summary) && (
                                                <button
                                                    className="icon-btn expand-btn"
                                                    onClick={() => toggleRowExpansion(job.id)}
                                                    title={expandedRows.has(job.id) ? 'Collapse Details' : 'Expand Details'}
                                                >
                                                    <i className={`fa ${expandedRows.has(job.id) ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                                                </button>
                                            )}
                                        </td>
                                        {/* 7. Conditionally render the actions cell and buttons inside it */}
                                        {(hasPermission('JOBS', 'can_edit') || hasPermission('JOBS', 'can_delete') || hasPermission('JOBS', 'can_view')) && (
                                            <td className="actions">
                                                <button className="icon-btn view-btn" onClick={() => handleViewJob(job)} title="View">
                                                    <i className="fa fa-eye"></i>
                                                </button>
                                                {hasPermission('JOBS', 'can_edit') && (
                                                    <button className="icon-btn edit-btn" onClick={() => handleEditJob(job)} title="Edit">
                                                        <i className="fa fa-pencil-alt"></i>
                                                    </button>
                                                )}
                                                {hasPermission('JOBS', 'can_delete') && (
                                                    <button className="icon-btn" onClick={() => handleOpenChangeCreator(job)} title="Change Creator" style={{ color: '#6f42c1' }}>
                                                        <i className="fa fa-user-edit"></i>
                                                    </button>
                                                )}
                                                {hasPermission('JOBS', 'can_delete') && (
                                                    <button
                                                        className={`icon-btn ${job.is_active === false ? 'activate-btn' : 'inactivate-btn'}`}
                                                        onClick={() => handleToggleActive(job)}
                                                        title={job.is_active === false ? 'Activate Job' : 'Inactivate Job'}
                                                    >
                                                        <i className={`fa ${job.is_active === false ? 'fa-check-circle' : 'fa-ban'}`}></i>
                                                    </button>
                                                )}
                                                {hasPermission('JOBS', 'can_delete') && (
                                                    <button className="icon-btn delete-btn" onClick={() => handleDeleteJob(job)} title="Delete">
                                                        <i className="fa fa-trash"></i>
                                                    </button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                    {expandedRows.has(job.id) && (
                                        <tr className="nested-details-row">
                                            <td colSpan="12">
                                                <div className="nested-details-content">
                                                    {/* Basic Job Information */}
                                                    <div className="detail-section">
                                                        <h4>Job Information:</h4>
                                                        <div className="detail-grid">
                                                            {job.email_subject && <div><strong>Email Subject:</strong> {job.email_subject}</div>}
                                                            {job.shipment_type && <div><strong>Shipment Type:</strong> {job.shipment_type}</div>}
                                                            {job.cargo_type && <div><strong>Cargo Type:</strong> {job.cargo_type}</div>}
                                                            {((job.shipment_type || '').toString().toUpperCase() === 'FCL' || (job.cargo_type || '').toString().toUpperCase() === 'FCL') && containerSummary(job) && <div><strong>Container Size:</strong> {containerSummary(job)}</div>}
                                                            {job.cargo_nature && <div><strong>Cargo Nature:</strong> {job.cargo_nature}</div>}
                                                            {job.status && job.status.length > 0 && <div><strong>Status:</strong> {job.status.join(', ')}</div>}
                                                            {job.kk_file_no && <div><strong>K&K File No:</strong> {job.kk_file_no}</div>}
                                                            {job.hbl_so_no && <div><strong>HBL/SO No:</strong> {job.hbl_so_no}</div>}
                                                            {job.vessel_details.eta && <div><strong>Vessel ETA:</strong> {formatDate(job.vessel_details.eta)}</div>}
                                                        </div>
                                                    </div>                                                    
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                    </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', padding: '10px 0' }}>
                          <div>
                              Showing {indexOfFirstJob} to {indexOfLastJob} of {totalCount} entries
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>Prev</button>
                              {getPageNumbers().map((pageNum, idx) => (
                                  pageNum === '...' ? (
                                      <span key={`ellipsis-${idx}`} style={{ padding: '0 6px' }}>...</span>
                                  ) : (
                                      <button
                                          key={pageNum}
                                          onClick={() => paginate(pageNum)}
                                          style={{
                                              fontWeight: currentPage === pageNum ? 'bold' : 'normal',
                                              textDecoration: currentPage === pageNum ? 'underline' : 'none'
                                          }}
                                      >
                                          {pageNum}
                                      </button>
                                  )
                              ))}
                              <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
                          </div>
                      </div>
                  </>
                  ) : (
                      <p className="no-data">No jobs found.</p>
                  )
              )}
              
            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <button className="modal-close-btn" onClick={() => handleFormClose(false)}>
                            &times;
                        </button>
                        <JobForm
                            mode={formMode}
                            initialData={currentJob}
                            onClose={handleFormClose}
                        />
                    </div>
                </div>
            )}

            {creatorModal.open && (
                <div className="modal-overlay" style={{ zIndex: 2000 }}>
                    <div className="dialog-container" style={{ width: '440px', maxWidth: '95vw' }}>
                        <div className="dialog-header">
                            <h3>Change Job Creator</h3>
                            <button
                                onClick={() => setCreatorModal({ open: false, job: null })}
                                style={{ background: 'none', border: 'none', color: '#fff', fontSize: '22px', cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}
                                title="Close"
                            >&times;</button>
                        </div>
                        <div className="dialog-body">
                            <p style={{ margin: '0 0 14px', fontSize: '13px', color: '#555' }}>
                                Job <strong>#{creatorModal.job?.id}</strong> &mdash; current creator: <strong>{creatorModal.job?.created_by_name || '—'}</strong>
                            </p>
                            {loadingUsers ? (
                                <p style={{ fontSize: '13px', color: '#888' }}>Loading users…</p>
                            ) : (
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>Select New Creator</label>
                                    <select
                                        value={selectedUserId}
                                        onChange={e => setSelectedUserId(e.target.value)}
                                        style={{ width: '100%', padding: '6px 8px', fontSize: '13px', borderRadius: '4px', border: '1px solid #ccc' }}
                                    >
                                        <option value="">— Select User —</option>
                                        {users.map(u => (
                                            <option key={u.id} value={u.id}>
                                                {[u.first_name, u.last_name].filter(Boolean).join(' ') || u.username}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                        <div className="dialog-footer">
                            <button
                                className="dialog-btn dialog-btn-secondary"
                                onClick={() => setCreatorModal({ open: false, job: null })}
                                disabled={savingCreator}
                            >
                                Cancel
                            </button>
                            <button
                                className="dialog-btn dialog-btn-primary"
                                onClick={handleSaveCreator}
                                disabled={!selectedUserId || savingCreator || loadingUsers}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {confirmCreatorModal.open && (
                <div className="modal-overlay" style={{ zIndex: 2100 }}>
                    <div className="dialog-container" style={{ width: '400px', maxWidth: '95vw' }}>
                        <div className="dialog-header">
                            <h3>Confirm Change</h3>
                        </div>
                        <div className="dialog-body">
                            <p style={{ margin: 0, fontSize: '14px', color: '#333' }}>
                                Are you sure you want to change the creator of Job <strong>#{creatorModal.job?.id}</strong> to <strong>{confirmCreatorModal.userName}</strong>?
                            </p>
                        </div>
                        <div className="dialog-footer">
                            <button
                                className="dialog-btn dialog-btn-secondary"
                                onClick={() => setConfirmCreatorModal({ open: false, userId: null, userName: '' })}
                                disabled={savingCreator}
                            >
                                Cancel
                            </button>
                            <button
                                className="dialog-btn dialog-btn-primary"
                                onClick={handleConfirmCreatorChange}
                                disabled={savingCreator}
                            >
                                {savingCreator ? 'Saving…' : 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {nominationModal && (
                <div className="modal-overlay" style={{ zIndex: 2000 }}>
                    <div className="dialog-container" style={{ width: '440px', maxWidth: '95vw' }}>
                        <div className="dialog-header">
                            <h3>Add Nomination</h3>
                            <button
                                onClick={() => setNominationModal(false)}
                                style={{ background: 'none', border: 'none', color: '#fff', fontSize: '22px', cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}
                                title="Close"
                            >&times;</button>
                        </div>
                        <div className="dialog-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>BL Number <span style={{ color: '#dc3545' }}>*</span></label>
                                <input
                                    type="text"
                                    className="search-input"
                                    style={{ width: '100%', minWidth: 0 }}
                                    value={nominationForm.bl_number}
                                    onChange={e => setNominationForm(f => ({ ...f, bl_number: e.target.value }))}
                                    placeholder="Enter BL Number"
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>Customer <span style={{ color: '#dc3545' }}>*</span></label>
                                <Select2Dropdown
                                    options={nomCustomers}
                                    value={nominationForm.customer}
                                    onChange={val => setNominationForm(f => ({ ...f, customer: val }))}
                                    valueKey="id"
                                    displayKey="text"
                                    placeholder="Select Customer"
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>Vessel</label>
                                <Select2Dropdown
                                    options={nomVessels}
                                    value={nominationForm.vessel}
                                    onChange={val => setNominationForm(f => ({ ...f, vessel: val }))}
                                    valueKey="id"
                                    displayKey="text"
                                    placeholder="Select Vessel"
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>Folder <span style={{ color: '#dc3545' }}>*</span></label>
                                <input
                                    type="text"
                                    className="search-input"
                                    style={{ width: '100%', minWidth: 0 }}
                                    value={nominationForm.folder_name}
                                    onChange={e => setNominationForm(f => ({ ...f, folder_name: e.target.value }))}
                                    placeholder="Enter Folder Name"
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>Shipping Line <span style={{ color: '#dc3545' }}>*</span></label>
                                <Select2Dropdown
                                    options={nomShippingLines}
                                    value={nominationForm.shipping_line}
                                    onChange={val => setNominationForm(f => ({ ...f, shipping_line: val }))}
                                    valueKey="id"
                                    displayKey="text"
                                    placeholder="Select Shipping Line"
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>ETA{nominationForm.vessel && nominationForm.vessel !== 'UNKNOWN' && <span style={{ color: '#dc3545' }}> *</span>}</label>
                                <input
                                    type="date"
                                    className="search-input"
                                    style={{ width: '100%', minWidth: 0 }}
                                    min={(() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; })()}
                                    value={nominationForm.eta}
                                    onChange={e => setNominationForm(f => ({ ...f, eta: e.target.value }))}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>Nominated ICD <span style={{ color: '#dc3545' }}>*</span></label>
                                <Select2Dropdown
                                    options={nomIcds}
                                    value={nominationForm.icd}
                                    onChange={val => setNominationForm(f => ({ ...f, icd: val }))}
                                    valueKey="id"
                                    displayKey="text"
                                    placeholder="Select ICD"
                                />
                            </div>
                        </div>
                        <div className="dialog-footer">
                            <button
                                className="dialog-btn dialog-btn-secondary"
                                onClick={() => setNominationModal(false)}
                                disabled={nomSaving}
                            >
                                Cancel
                            </button>
                            <button
                                className="dialog-btn dialog-btn-primary"
                                onClick={handleSaveNomination}
                                disabled={nomSaving}
                            >
                                {nomSaving ? 'Saving…' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={confirmDelete.open}
                title="Delete Job"
                message={`Are you sure you want to delete Job #${confirmDelete.job?.id}?`}
                onConfirm={handleConfirmDelete}
                onCancel={() => setConfirmDelete({ open: false, job: null })}
            />

            <ConfirmDialog
                open={confirmToggleActive.open}
                title={confirmToggleActive.job?.is_active === false ? 'Activate Job' : 'Inactivate Job'}
                message={`Are you sure you want to ${confirmToggleActive.job?.is_active === false ? 'activate' : 'inactivate'} Job #${confirmToggleActive.job?.id}?`}
                onConfirm={handleConfirmToggleActive}
                onCancel={() => setConfirmToggleActive({ open: false, job: null })}
            />
        </div>
    );
};

export default JobList;
